import {Observable} from 'rxjs';
import {
  EmployeeResponse,
  RoomType,
  Workspace,
  WorkspaceCoordinate,
  AvailableRoomsAndWorkspaces,
} from '@innowise-group/core';

export type WorkspaceBookType =
  | 'Booked'
  | 'Occupied'
  | 'Reserved'
  | 'Free'
  | 'Remote'
  | 'Coworking'
  | 'Coworking Free'
  | 'Coworking Booked';

export interface WorkspacesApi {
  editWorkspace: (workspaceId: string, workspaceData: object) => Observable<unknown>;
  editWorkspaceWithRequests: (workspaceId: string, workspaceData: Partial<WorkspaceToUpdate>) => Observable<void>;
  deleteWorkspaces: (workspaceId: Array<string>) => Observable<unknown>;
  createWorkspaces: (workspaces: WorkspaceToCreate[]) => Observable<void>;
  updateWorkspaces: (workspaces: WorkspaceToUpdate[]) => Observable<void>;
  getWorkspacesByFloor: (floorId: string) => Observable<WorkspaceResponse[]>;
  getWorkspaceResponseById: (id: string) => Observable<WorkspaceResponse>;
  createWorkspace: (workspace: WorkspaceToCreate) => Observable<WorkspaceResponse>;
  patchWorkspace: (workspaceId: string, workspaceData: Partial<WorkspaceToUpdate>) => Observable<WorkspaceResponse>;
  deleteWorkspace: (workspaceId: string) => Observable<void>;
  rotateWorkspace: (
    workspaceId: string,
    rotateDegree: number,
    status: WorkspaceBookType,
  ) => Observable<WorkspaceResponse>;
  bookingWorkspaceMode: (floorId: string, start: string, end: string) => Observable<Workspace[]>;
  getWorkspaces: () => Observable<Workspace[]>;
  getWorkspaceById: (id: string) => Observable<Workspace>;
  getWorkspacesFromFloor: (id: string) => Observable<Workspace[]>;
  getAvailableRoomsAndWorkspaces: (
    floorId: string,
    startDate: string,
    endDate: string,
  ) => Observable<AvailableRoomsAndWorkspaces[]>;
}

export interface BookedStatusResponse {
  id: number;
  workspace: number;
  employee: EmployeeResponse;
  occupation_end_date: string;
  occupation_start_date: string;
  comment: string;
}

export interface WorkspaceResponse {
  employee: EmployeeResponse;
  id: number;
  number: number;
  occupation_end_date: string;
  occupation_start_date: string;
  occupation_type: null;
  room: number;
  room_number: string;
  room_type: RoomType;
  status: WorkspaceBookType;
  comment: string;
  coordinates: WorkspaceCoordinate;
  booked_status: BookedStatusResponse[];
  active_requests: BookedStatusResponse[];
  blue?: boolean;
  rotate_degree: number;
}

export interface WorkspaceToCreate {
  employee: number | null;
  number?: number | null;
  occupation_end_date: string | null;
  occupation_start_date: string | null;
  occupation_type?: string;
  /** Ignored by backend for non-virtual floors; room is resolved from coordinates. */
  room?: number | null;
  floor: number;
  status: WorkspaceBookType;
  comment: string | null;
  coordinates: WorkspaceCoordinate;
  rotate_degree: number;
}

export interface WorkspaceToUpdate extends Omit<WorkspaceToCreate, 'floor'> {
  id: number;
  floor?: number;
  room?: number | null;
}

export enum WorkspaceStatuses {
  Booked = 'Booked',
  Occupied = 'Occupied',
  Reserved = 'Reserved',
  Free = 'Free',
  Remote = 'Remote',
  CoworkingFree = 'Coworking Free',
  CoworkingBooked = 'Coworking Booked',
}
